// @core/stores/theme.ts
// Auto-generated by corebuilder. Safe to customize — will not be overwritten on regeneration.

import { makeAutoObservable, runInAction } from 'mobx';

// ============================================
// Types
// ============================================

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeColors {
  // Primary colors
  primary: string;
  primaryForeground: string;

  // Secondary colors
  secondary: string;
  secondaryForeground: string;

  // Background colors
  background: string;
  foreground: string;

  // Muted colors
  muted: string;
  mutedForeground: string;

  // Accent colors
  accent: string;
  accentForeground: string;

  // Destructive colors
  destructive: string;
  destructiveForeground: string;

  // Border and input
  border: string;
  input: string;
  ring: string;

  // Card
  card: string;
  cardForeground: string;

  // Popover
  popover: string;
  popoverForeground: string;
}

export interface ThemeConfig {
  light: Partial<ThemeColors>;
  dark: Partial<ThemeColors>;
}

// ============================================
// Default Theme Colors
// ============================================

const defaultLightColors: ThemeColors = {
  primary: '222.2 47.4% 11.2%',
  primaryForeground: '210 40% 98%',
  secondary: '210 40% 96.1%',
  secondaryForeground: '222.2 47.4% 11.2%',
  background: '0 0% 100%',
  foreground: '222.2 84% 4.9%',
  muted: '210 40% 96.1%',
  mutedForeground: '215.4 16.3% 46.9%',
  accent: '210 40% 96.1%',
  accentForeground: '222.2 47.4% 11.2%',
  destructive: '0 84.2% 60.2%',
  destructiveForeground: '210 40% 98%',
  border: '214.3 31.8% 91.4%',
  input: '214.3 31.8% 91.4%',
  ring: '222.2 47.4% 11.2%',
  card: '0 0% 100%',
  cardForeground: '222.2 47.4% 11.2%',
  popover: '0 0% 100%',
  popoverForeground: '222.2 47.4% 11.2%',
};

const defaultDarkColors: ThemeColors = {
  primary: '222.2 47.4% 51.2%',
  primaryForeground: '210 40% 98%',
  secondary: '220 3% 17%',
  secondaryForeground: '210 5% 90%',
  background: '220 4% 10%',
  foreground: '210 5% 90%',
  muted: '220 3% 17%',
  mutedForeground: '220 2% 52%',
  accent: '220 3% 20%',
  accentForeground: '210 5% 90%',
  destructive: '0 62% 35%',
  destructiveForeground: '210 5% 90%',
  border: '220 3% 19%',
  input: '220 3% 17%',
  ring: '220 4% 62%',
  card: '220 4% 13%',
  cardForeground: '210 5% 90%',
  popover: '220 4% 13%',
  popoverForeground: '210 5% 90%',
};

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEY = 'app_theme';

// ============================================
// Theme Store
// ============================================

class ThemeStore {
  mode: ThemeMode = 'system';
  isInitialized = false;

  private lightColors: ThemeColors = { ...defaultLightColors };
  private darkColors: ThemeColors = { ...defaultDarkColors };
  private mediaQuery: MediaQueryList | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // ============================================
  // Computed Properties
  // ============================================

  /**
   * Get the resolved theme (light or dark)
   * If mode is 'system', returns the system preference
   */
  get resolvedTheme(): ResolvedTheme {
    if (this.mode === 'system') {
      return this.getSystemTheme();
    }
    return this.mode;
  }

  /**
   * Check if dark mode is active
   */
  get isDark(): boolean {
    return this.resolvedTheme === 'dark';
  }

  /**
   * Get current theme colors
   */
  get colors(): ThemeColors {
    return this.isDark ? this.darkColors : this.lightColors;
  }

  // ============================================
  // Initialization
  // ============================================

  /**
   * Initialize theme store
   * Sets up system theme detection and applies saved theme
   */
  initialize(config?: ThemeConfig): void {
    // Apply custom colors if provided
    if (config?.light) {
      this.lightColors = { ...defaultLightColors, ...config.light };
    }
    if (config?.dark) {
      this.darkColors = { ...defaultDarkColors, ...config.dark };
    }

    // Load saved theme from storage
    this.loadFromStorage();

    // Set up system theme detection
    this.setupSystemThemeDetection();

    // Apply theme to DOM
    this.applyTheme();

    runInAction(() => {
      this.isInitialized = true;
    });
  }

  /**
   * Cleanup listeners
   */
  dispose(): void {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleSystemThemeChange);
    }
  }

  // ============================================
  // Storage Management
  // ============================================

  private loadFromStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      const savedTheme = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
        this.mode = savedTheme;
      }
    } catch {
      // Use default theme
    }
  }

  private saveToStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, this.mode);
    } catch {
      // Ignore storage errors
    }
  }

  // ============================================
  // System Theme Detection
  // ============================================

  private getSystemTheme(): ResolvedTheme {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private setupSystemThemeDetection(): void {
    if (typeof window === 'undefined') return;

    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaQuery.addEventListener('change', this.handleSystemThemeChange);
  }

  private handleSystemThemeChange = (): void => {
    if (this.mode === 'system') {
      this.applyTheme();
    }
  };

  // ============================================
  // Theme Application
  // ============================================

  private applyTheme(): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const theme = this.resolvedTheme;
    const colors = this.colors;

    // Set theme class
    root.classList.remove('light', 'dark');
    root.classList.add(theme);

    // Set color-scheme for native elements
    root.style.colorScheme = theme;

    // Apply CSS custom properties
    Object.entries(colors).forEach(([key, value]) => {
      const cssVar = `--${this.camelToKebab(key)}`;
      root.style.setProperty(cssVar, value);
    });
  }

  private camelToKebab(str: string): string {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  }

  // ============================================
  // Actions
  // ============================================

  /**
   * Set the theme mode
   */
  setMode(mode: ThemeMode): void {
    runInAction(() => {
      this.mode = mode;
      this.saveToStorage();
      this.applyTheme();
    });
  }

  /**
   * Toggle between light and dark themes
   * If currently using system, switches to the opposite of current system theme
   */
  toggle(): void {
    const current = this.resolvedTheme;
    this.setMode(current === 'dark' ? 'light' : 'dark');
  }

  /**
   * Update custom colors
   */
  setColors(config: ThemeConfig): void {
    runInAction(() => {
      if (config.light) {
        this.lightColors = { ...this.lightColors, ...config.light };
      }
      if (config.dark) {
        this.darkColors = { ...this.darkColors, ...config.dark };
      }
      this.applyTheme();
    });
  }

  /**
   * Reset to default colors
   */
  resetColors(): void {
    runInAction(() => {
      this.lightColors = { ...defaultLightColors };
      this.darkColors = { ...defaultDarkColors };
      this.applyTheme();
    });
  }
}

// Singleton instance
export const themeStore = new ThemeStore();
